import { Injectable } from '@angular/core';
import {
	ComponentController,
	Notification,
	filterNil,
} from '@lycoris-nubila/ngx-lycoris';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import sortBy from 'lodash-es/sortBy';
import { Observable, combineLatest } from 'rxjs';
import {
	filter,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs/operators';

import { CommandEvent } from 'application/common/model/command-event';
import { NotificationDomain } from 'application/common/model/notification-domain';
import { ModuleSort } from 'module/model/module.sort';
import { ModuleApplicationPort } from 'module/port/module-application.port';
import { OrganizationPropertyName } from 'organization/model/organization-property-name';
import { IPhase } from 'phase/phase';
import { PhaseApplicationPort } from 'phase/port/phase-application.port';
import { AppSidebarController } from 'presentation/app-sidebar/app-sidebar.controller';
import { SidebarPlanModule } from 'presentation/app-sidebar/sidebar-coaching-plan/coaching-plan.models';
import { AppService } from 'presentation/app.service';
import { UserApplicationPort } from 'user/port/user-application.port';

import { CoachingPlanStore, SidebarPlanState } from './coaching-plan.store';

@Injectable()
@UntilDestroy()
export class CoachingPlanController extends ComponentController<SidebarPlanState> {
	public phases$: Observable<IPhase[]>;
	public activePhase$: Observable<IPhase>;
	public activePhaseIndex$: Observable<number>;
	public showLibraryButton$: Observable<boolean>;
	public libraryRouterLink$: Observable<string[]>;
	public modules$: Observable<SidebarPlanModule[]>;

	constructor(
		store: CoachingPlanStore,
		private _appService: AppService,
		private _userApplicationPort: UserApplicationPort,
		private _appSidebarController: AppSidebarController,
		private _phaseApplicationPort: PhaseApplicationPort,
		private _moduleApplicationPort: ModuleApplicationPort
	) {
		super(store);

		this._initObservables();
		this._initListeners();
	}

	public switchTab(index: number) {
		this.store.update({ activePhaseIndex: index });
	}

	public sectionModule$(section) {
		return this.modules$.pipe(
			map((modules) =>
				modules.filter((module) => module.instance.sectionId === section.id)
			),
			shareReplay({ refCount: true, bufferSize: 1 })
		);
	}

	private _initObservables() {
		const user$ = this._appSidebarController.userId$.pipe(
			switchMap((userId) => this._userApplicationPort.observeUserById(userId)),
			map((data) => data.data),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.activePhaseIndex$ = this.select('activePhaseIndex').pipe(
			map((activeTabIndex) => activeTabIndex ?? 0),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		const sessionUser$ = this._userApplicationPort
			.observeSessionUser()
			.pipe(shareReplay({ bufferSize: 1, refCount: true }));

		const sessionOrganization$ = sessionUser$.pipe(
			switchMap((user) => user.organization$),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.phases$ = this._appSidebarController.userId$.pipe(
			filterNil(),
			switchMap((userId) =>
				this._phaseApplicationPort
					.observePhasesByUserId(userId)
					.pipe(map((data) => data.data))
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.activePhase$ = combineLatest([
			this.phases$,
			this.activePhaseIndex$,
		]).pipe(
			map(([phases, activePhaseIndex]) => phases[activePhaseIndex]),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.libraryRouterLink$ = this._appSidebarController.userId$.pipe(
			map((userId) => ['/library', userId]),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.showLibraryButton$ = combineLatest([
			sessionOrganization$.pipe(filterNil()),
			sessionUser$.pipe(filterNil()),
			user$.pipe(filterNil()),
		]).pipe(
			map(
				([sessionOrganization, sessionUser, user]) =>
					(user.id === sessionUser.id &&
						sessionOrganization.isPropertyTrue(
							OrganizationPropertyName.MODULE_LIBRARY_COACHEE_ACCESS_ENABLED,
							true
						)) ||
					sessionUser.isCoachOf(user)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);

		this.modules$ = combineLatest([
			sessionUser$.pipe(filterNil()),
			this._appSidebarController.userId$.pipe(filterNil()),
			this._userApplicationPort.observeUserModuleSuggestions(),
			this._appService.currentLang$,
			this._userApplicationPort
				.observeUserPlanChanges()
				.pipe(startWith(null as Notification<NotificationDomain, null>)),
			this._userApplicationPort.commandHandled$.pipe(
				filter((e) => e.method === 'updateUserLanguage'),
				startWith(null as CommandEvent<unknown>)
			),
		]).pipe(
			switchMap(([sessionUser, userId, userModuleSuggestions, currentLang]) =>
				this._userApplicationPort.observeUserModules(userId).pipe(
					map((data) => data.data),
					filter((userModules) => (userModules || []).length > 0),
					switchMap((userModules) =>
						this._moduleApplicationPort
							.observeUserModulesByIds({
								ids: userModules.map((userModule) => userModule.moduleId),
								sort: ModuleSort.NAME,
								sortNameByLocale: sessionUser.metadata?.language ?? currentLang,
							})
							.pipe(
								map((data) => sortBy(data.data, 'position')),
								map((modules) =>
									modules
										.filter((module) => module.isVisibleInPlan)
										.map((module) => {
											const userModule = userModules.find(
												(um) => um.moduleId === module.id
											);
											return {
												isSuggestion: userModuleSuggestions.some(
													(s) => s.orgModuleId === module.id
												),
												routerLink: [module.linkRoot, module.id, 'user', userId],
												showSettings: module.hasSettings && sessionUser.isCoach,
												hasSettings: userModule.pages.length > 0,
												isStarted: userModule?.isStarted,
												isDone: userModule?.isDone,
												instance: module,
												userId,
											} as SidebarPlanModule;
										})
								)
							)
					)
				)
			),
			shareReplay({ bufferSize: 1, refCount: true })
		);
	}

	private _initListeners() {
		this._userApplicationPort
			.fetchUserModuleSuggestions()
			.pipe(untilDestroyed(this))
			.subscribe();
	}
}
